import { withLDProvider } from 'launchdarkly-react-client-sdk';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { assets, svg } from '@/_shared/assets';
import { FallbackError, LoadingPage } from '@/_shared/components';
import { bootstrap } from '@/_shared/configuration';
import { environment } from '@/_shared/configuration/env';
import { useInit } from '@/_shared/hooks';
import Routes from '@/_shared/route';
import { PlatformThemeProvider } from '@/_shared/styles/ThemeProvider';
import './index.css';

declare global {
  interface Window {
    accent?: string;
  }
}

const { store, persistor } = bootstrap();

const App = withLDProvider({
  clientSideID: environment.VITE_APP_LD || '',
  options: {
    bootstrap: {
      tutorial: false,
      maintenanceMode: false,
      chatbot: false,
      dashboard: true,
      organization: true,
      team: true,
      people: true,
      survey: true,
      aspect: true,
      switchOrganization: false,
      publicDocs: true,
      payments: true,
      registration: true,
    },
  },
})(() => {
  const { isSuccess, isLoading } = useInit();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isSuccess) {
    return <Routes />;
  }

  return <LoadingPage />;
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Provider store={store}>
    <PersistGate persistor={persistor}>
      <PlatformThemeProvider primaryAsAccent assets={assets} svg={svg}>
        <ErrorBoundary FallbackComponent={FallbackError}>
          <App />
        </ErrorBoundary>
      </PlatformThemeProvider>
    </PersistGate>
  </Provider>,
);
